'use client';

import { useRouter } from '@bprogress/next/app';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { GTMEventAuth, sendGTMEventAuth } from '@/fo/api/gtm';
import { FeatureAuthFormForgotPassword } from '@/fo/feature/auth/form/forgot-password';
import { FeatureAuthFormLogin } from '@/fo/feature/auth/form/login';
import { FeatureAuthFormRegistration } from '@/fo/feature/auth/form/registration';
import { UiAuthModalBase } from '@/fo/ui/auth/modal/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';

export interface FeatureAuthModalBaseProps {
  isAuthorized?: boolean;
  agreementPrivacy: string;
}

export function FeatureAuthModalBase({
  isAuthorized,
  agreementPrivacy
}: FeatureAuthModalBaseProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const refReferral = useRef<GTMEventAuth['referral']>('header');
  const refAuthorized = useRef(isAuthorized);
  const tabRef = useRef<'login' | 'registration'>('login');
  /** Не скрываем модалку до закрытия пользователем после успешной авторизации */
  const refJustLoggedIn = useRef(false);
  const [, forceRender] = useState(0);

  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (!authParam) return;

    if (!isAuthorized) {
      if (authParam === 'open') {
        utilEventAuthModal.open('header');
      }

      if (authParam === 'forgot-password') {
        utilEventAuthModal.open('header');
        utilEventAuthModal.showForgotPassword();
      }
    }

    const params = new URLSearchParams(searchParams);
    params.delete('auth');
    const query = params.toString();
    router.replace(`${window.location.pathname}${query ? `?${query}` : ''}`, {
      scroll: false
    });
  }, [isAuthorized, searchParams, router]);

  useEffect(() => {
    if (isAuthorized) return;

    const unsubscribeOnOpen = utilEventAuthModal.onOpen((referral) => {
      refReferral.current = referral;
    });
    return () => {
      unsubscribeOnOpen();
    };
  }, [isAuthorized]);

  useEffect(() => {
    if (isAuthorized && refAuthorized.current !== isAuthorized) {
      const referral = refReferral.current;
      sendGTMEventAuth({
        event: tabRef.current === 'login' ? 'authorization' : 'registration',
        referral
      });
      refReferral.current = 'header';
      tabRef.current = 'login';
      refJustLoggedIn.current = true;

      try {
        const redirectTo = sessionStorage.getItem('postAuthRedirect');
        if (redirectTo) {
          sessionStorage.removeItem('postAuthRedirect');
          if (referral !== 'cart') {
            router.push(redirectTo);
          }
        }
      } catch {
        // ignore
      }
    }
    refAuthorized.current = isAuthorized;
  }, [isAuthorized, router]);

  const handleClose = () => {
    refJustLoggedIn.current = false;
    forceRender((n) => n + 1);
  };

  if (isAuthorized && !refAuthorized.current) {
    refJustLoggedIn.current = true;
  }
  if (isAuthorized && !refJustLoggedIn.current) {
    return null;
  }

  return (
    <UiAuthModalBase
      loginForm={<FeatureAuthFormLogin />}
      registrationForm={
        <FeatureAuthFormRegistration agreementPrivacy={agreementPrivacy} />
      }
      forgotPasswordForm={<FeatureAuthFormForgotPassword />}
      onTabChange={(tabName) => (tabRef.current = tabName)}
      onClose={handleClose}
    />
  );
}
